export interface IMaybe<T> {
    read<TResult>(fn: (arg: T) => TResult): IMaybe<TResult>;

    exec(fn: (arg: T) => void): IMaybe<T>;

    valueOrDefault(defaultValue: T): T;

    readonly value: T;
    readonly isNothing: boolean;

    get<P extends keyof T>(name: P): IMaybe<T[P]>;

    set<P extends keyof T>(name: P, value: T[P]): IMaybe<T>;
}

class MaybeImpl<T> implements IMaybe<T> {
    constructor(readonly value: T) {}

    public read<TResult>(fn: (arg: T) => TResult): IMaybe<TResult> {
        return this.isNothing ? Maybe.nothing : Maybe(fn(this.value));
    }

    public exec(fn: (arg: T) => void): IMaybe<T> {
        if (!this.isNothing) {
            fn(this.value);
        }
        return this;
    }

    public get<P extends keyof T>(name: P): IMaybe<T[P]> {
        return this.isNothing ? Maybe.nothing : Maybe(this.value[name]);
    }

    public set<P extends keyof T>(name: P, value: T[P]): IMaybe<T> {
        if (!this.isNothing) {
            this.value[name] = value;
        }
        return this;
    }

    public get isNothing(): boolean {
        return this.value == null;
    }

    public valueOrDefault(defaultValue: T): T {
        return this.isNothing ? defaultValue : this.value;
    }
}

interface IMaybeType {
    <TMaybe>(val: TMaybe): IMaybe<TMaybe>;

    readonly nothing: IMaybe<never>;
}

const Maybe = (() => {
    const maybeConstructor = <T>(val: T) => new MaybeImpl<T>(val);
    (maybeConstructor as any).nothing = new MaybeImpl(null);
    return maybeConstructor;
})() as IMaybeType;

export default Maybe;
